import React, { useState } from 'react';
import { ArrowLeft, X, MessageSquare, ChevronLeft, ChevronRight, Phone, Mail, Briefcase, DollarSign, User, Calendar, Activity as ActivityIcon, AlertTriangle, TrendingUp, UserCheck } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { leadsAPI } from '../../api/apiHelper';
import DealOverview from './LeadDetailTabs/DealOverview';
import DealHealth from './LeadDetailTabs/DealHealth';
// import Stakeholders from './LeadDetailTabs/Stakeholders';
import Activity from './LeadDetailTabs/Activity';
import Products from './LeadDetailTabs/Products';

import AIInsights from './LeadDetailTabs/AIInsights';

export default function LeadDetail({ onBack }) {
  const { id } = useParams();
  const location = useLocation();
  const initialDeal = location.state?.deal || null;
  const [activeTab, setActiveTab] = useState(() => {
    // Restore tab from sessionStorage when returning from a sub-page (e.g. Coaching)
    const saved = sessionStorage.getItem(`leadDetailTab_${id}`);
    if (saved) { sessionStorage.removeItem(`leadDetailTab_${id}`); return saved; }
    return location.state?.fromTab || "Deal overview";
  });
  const navigate = useNavigate();
  const [dealData, setDealData] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    const fetchDealData = async () => {
      try {
        setLoading(true);
        if (id && id !== 'view') {
          const response = await leadsAPI.getDealInfo(id);
          if (response && response.data) {
            setDealData(response.data);
          }

          // console.log("Fetched deal data0099999999999999:", response.data);
        }
      } catch (error) {
        console.error("Failed to fetch deal info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDealData();
  }, [id]);

  const tabs = [
    "Deal overview",
    "Deal health",
    // "Stakeholders",
    "Activity",
    "Products",
    "AI Insights"
  ];

  // Helper to generate initials from a name
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper to format display values responsibly
  const getDisplayValue = (value, type = 'text') => {
    if (value === undefined || value === null || value === '' || value === 0 && type === 'text') {
      return <span style={ { color: '#9ca3af', fontStyle: 'italic', fontSize: '12px' } }>No input</span>;
    }

    if (type === 'date') {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return <span style={ { color: '#9ca3af', fontStyle: 'italic', fontSize: '12px' } }>No input</span>;
      }
      // If type is date, formatting is handled in render, return raw valid date or fallback
      return value;
    }

    return value;
  };

  const getCallScoreColor = (score) => {
    if (score === null || score === undefined) return '#6b7280';
    const s = Number(score);
    if (isNaN(s)) return '#6b7280';
    if (s >= 7) return '#22c55e'; // High (Green)
    if (s >= 4) return '#2196f3'; // Medium (Blue)
    return '#ff9800'; // Low (Orange)
  };

  const getRiskColor = (score) => {
    if (score === null || score === undefined) return '#6b7280';
    const s = Number(score);
    if (isNaN(s)) return '#6b7280';
    if (s < 40) return '#22c55e'; // Low (Green)
    if (s < 70) return '#eab308'; // Medium (Yellow)
    return '#ef4444'; // High (Red)
  };

  // Safe extraction helper
  const getInitialContact = () => {
    if (initialDeal?.contactNumbers?.[0]) return initialDeal.contactNumbers[0];
    if (initialDeal?.clientCompany) return initialDeal.clientCompany;
    return {};
  };
  const initContact = getInitialContact();

  // Enhance leadData with API structure mapping -> State Fallback (No dummy data)
  const displayData = {
    ownerName: dealData?.clientCompany?.name || initContact.name || "",
    companyName: dealData?.clientCompany?.companyName || initialDeal?.company || "",
    contactOwner: {
      name: dealData?.createdBy?.name || "",
      phone: dealData?.clientCompany?.number || initContact.number || "",
      email: dealData?.clientCompany?.email || initContact.email || "",
    },
    dealName: dealData?.dealName || initialDeal?.deal || "",
    dealValue: dealData?.value || (initialDeal?.amount ? parseFloat(String(initialDeal.amount).replace(/[^0-9.]/g, '')) : undefined) || 0,
    assignedTo: {
      name: dealData?.assignedTo?.name || (initialDeal?.assignedTo?.name) || "",
      email: dealData?.assignedTo?.email || (initialDeal?.assignedTo?.email) || ""
    },
    riskScore: {
      label: dealData?.riskLevel ? (dealData.riskLevel.charAt(0).toUpperCase() + dealData.riskLevel.slice(1)) : (initialDeal?.risk >= 70 ? 'High' : initialDeal?.risk >= 40 ? 'Medium' : 'Low'),
      value: dealData?.riskScore !== undefined ? dealData.riskScore : (initialDeal?.risk !== undefined ? initialDeal.risk : null)
    },
    customerEngagementScore: dealData?.engagementScore !== undefined ? dealData.engagementScore : (initialDeal?.engagementScore !== undefined ? initialDeal.engagementScore : null),
    callScore: dealData?.callScore !== undefined ? dealData.callScore : (initialDeal?.callScore !== undefined ? initialDeal.callScore : null),
    nextCallMeetingDate: dealData?.nextStepDate || initialDeal?.nextActivity || "",
    lastActivityDate: dealData?.updatedAt || initialDeal?.lastActivity || "",
    meddicc: dealData?.meddicStatus ? {
      metrics: { present: dealData.meddicStatus.metrics?.present },
      economic_buyer: { present: dealData.meddicStatus.economic_buyer?.present },
      decision_criteria: { present: dealData.meddicStatus.decision_criteria?.present },
      decision_process: { present: dealData.meddicStatus.decision_process?.present },
      identified_pain: { present: dealData.meddicStatus.identified_pain?.present },
      champion: { present: dealData.meddicStatus.champion?.present },
    } : (initialDeal?.meddic ? {
      metrics: { present: initialDeal.meddic.metrics?.present ?? initialDeal.meddic.metrics },
      economic_buyer: { present: initialDeal.meddic.economic_buyer?.present ?? initialDeal.meddic.economicBuyer },
      decision_criteria: { present: initialDeal.meddic.decision_criteria?.present ?? initialDeal.meddic.decisionCriteria },
      decision_process: { present: initialDeal.meddic.decision_process?.present ?? initialDeal.meddic.decisionProcess },
      identified_pain: { present: initialDeal.meddic.identified_pain?.present ?? initialDeal.meddic.identifyPain },
      champion: { present: initialDeal.meddic.champion?.present ?? initialDeal.meddic.champion },
    } : {}),
  };
  //    console.log("displayData:", displayData);
  //   console.log("meddicc full:", displayData.meddicc);
  // console.log("champion:", displayData.meddicc?.champion);
  // console.log("champion.present:", displayData.meddicc?.champion?.present);
  const meddiccKeys = [
    'metrics',
    'economic_buyer',
    'decision_criteria',
    'decision_process',
    'identified_pain',
    'champion'
  ];

  const [showMeddicInfo, setShowMeddicInfo] = useState(false);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (location.state?.fromPage === 'insights') {
      navigate('/home/insights');
    } else if (location.state?.fromPage === 'pipeline') {
      navigate('/home', { state: { activeTab: 'pipeline' } });
    } else if (location.state?.fromPage === 'manager-dashboard' || location.state?.from === 'manager-dashboard') {
      navigate('/home', { state: { activeTab: 'manager' } });
    } else {
      navigate('/home', { state: { activeTab: 'pipeline' } });
    }
  };

  return (
    <div style={ {
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      background: '#f4f6f8',
      fontFamily: 'Instrument Sans, sans-serif',
      overflow: 'hidden',
      minHeight: 0,
    } } >

      {/* Compact Top Bar */ }
      < div style={ {
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        flexShrink: 0
      }
      }>
        <button
          onClick={ handleBack }
          style={ {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: '#ffffff',
            border: '1px solid #d1d5db',
            padding: '4px 12px',
            borderRadius: '6px',
            cursor: 'pointer',
            color: '#374151',
            fontSize: '12px',
            fontWeight: 600,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            transition: 'all 0.2s ease'
          } }
        >
          <ArrowLeft size={ 14 } />
          Back
        </button>
      </div >

      <div style={ { display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 } }>

        {/* Main Scrollable Content Area */ }
        <div style={ { flex: 1, overflow: 'hidden', display: 'flex', padding: '8px 8px 0 8px', gap: '8px', flexWrap: 'wrap', minHeight: 0 } }>

          {/* Left Sidebar - Compact */ }
          <div style={ { width: 'clamp(200px, 25vw, 250px)', display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0, minWidth: 0, overflowY: 'auto', overflowX: 'hidden', paddingBottom: '24px', minHeight: 0 } }>

            {/* Section 1: Contact Info */ }
            <div style={ { background: '#fff', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', flex: '0 0 auto', overflow: 'hidden', minWidth: 0 } }>

              {/* Header: Avatar, Name, Company */ }
              <div style={ { display: 'flex', gap: '12px', marginBottom: '20px', alignItems: 'center' } }>
                <div style={ { width: '48px', height: '48px', borderRadius: '50%', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4338ca', fontWeight: 600, fontSize: '18px' } }>
                  { getInitials(displayData.ownerName) }
                </div>
                <div>
                  <div style={ { fontWeight: 700, color: '#111827', fontSize: '15px' } }>{ getDisplayValue(displayData.ownerName) }</div>
                  <div style={ { color: '#6b7280', fontSize: '13px' } }>{ getDisplayValue(displayData.companyName) }</div>
                </div>
              </div>

              {/* Fields List */ }
              <div style={ { display: 'flex', flexDirection: 'column', gap: '12px' } }>

                {/* Contact Owner */ }
                <div style={ { display: 'grid', gridTemplateColumns: '90px 1fr', alignItems: 'center', gap: '8px', minWidth: 0 } }>
                  <div style={ { fontSize: '12px', color: '#4b5563', fontWeight: 500 } }>Contact Owner</div>
                  <div style={ { fontSize: '13px', color: '#111827', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 } }>
                    { getDisplayValue(displayData.contactOwner.name) }
                  </div>
                </div>

                {/* Phone */ }
                <div style={ { display: 'grid', gridTemplateColumns: '90px 1fr', alignItems: 'center', gap: '8px', minWidth: 0 } }>
                  <div style={ { fontSize: '12px', color: '#4b5563', fontWeight: 500 } }>Phone</div>
                  <div style={ {
                    fontSize: '13px',
                    color: displayData.contactOwner.phone ? '#111827' : '#9ca3af',
                    fontWeight: displayData.contactOwner.phone ? 600 : 400,
                    background: displayData.contactOwner.phone ? '#eff6ff' : 'transparent',
                    padding: displayData.contactOwner.phone ? '4px 8px' : '0',
                    borderRadius: '4px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0
                  } }>
                    { getDisplayValue(displayData.contactOwner.phone) }
                  </div>
                </div>

                {/* Email */ }
                <div style={ { display: 'grid', gridTemplateColumns: '90px 1fr', alignItems: 'center', gap: '8px', minWidth: 0 } }>
                  <div style={ { fontSize: '12px', color: '#4b5563', fontWeight: 500 } }>Email</div>
                  <div
                    title={ displayData.contactOwner.email }
                    style={ {
                      fontSize: '13px',
                      color: displayData.contactOwner.email ? '#111827' : '#9ca3af',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: displayData.contactOwner.email ? 'pointer' : 'default',
                      fontWeight: displayData.contactOwner.email ? 600 : 400,
                      background: displayData.contactOwner.email ? '#eff6ff' : 'transparent',
                      padding: displayData.contactOwner.email ? '4px 8px' : '0',
                      borderRadius: '4px',
                      minWidth: 0,
                      maxWidth: '100%'
                    } }
                  >
                    { getDisplayValue(displayData.contactOwner.email) }
                  </div>
                </div>

                {/* Deal Name */ }
                <div style={ { display: 'grid', gridTemplateColumns: '90px 1fr', alignItems: 'start', gap: '8px' } }>
                  <div style={ { fontSize: '12px', color: '#4b5563', fontWeight: 500, paddingTop: '1px' } }>Deal Name</div>
                  <div style={ { fontSize: '13px', color: '#111827', wordBreak: 'break-word', overflow: 'hidden' } }>
                    { getDisplayValue(displayData.dealName) }
                  </div>
                </div>

                {/* Deal Value */ }
                <div style={ { display: 'grid', gridTemplateColumns: '90px 1fr', alignItems: 'start', gap: '8px' } }>
                  <div style={ { fontSize: '12px', color: '#4b5563', fontWeight: 500, paddingTop: '1px' } }>Deal Value</div>
                  <div style={ { fontSize: '13px', color: '#111827', fontWeight: 600, wordBreak: 'break-all', overflow: 'hidden' } }>
                    ${ (displayData.dealValue || 0).toLocaleString() }
                  </div>
                </div>

              </div>
            </div>

            {/* Section 2: Key Metrics */ }
            <div style={ { background: '#fff', borderRadius: '8px', padding: '12px 16px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '16px' } }>

              {/* 1. Assigned To */ }
              <div>
                <div style={ { fontSize: '10px', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' } }>ASSIGNED TO</div>
                <div style={ { display: 'flex', alignItems: 'center', gap: '10px' } }>
                  <div style={ { width: '32px', height: '32px', borderRadius: '50%', background: '#f3e8ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7e22ce', fontSize: '13px', fontWeight: 600 } }>
                    { getInitials(displayData.assignedTo.name) }
                  </div>
                  <div>
                    <div style={ { fontSize: '13px', fontWeight: 600, color: '#111827', lineHeight: '1.2' } }>{ getDisplayValue(displayData.assignedTo.name) }</div>
                    <div style={ { fontSize: '12px', color: '#6b7280', lineHeight: '1.2' } }>{ getDisplayValue(displayData.assignedTo.email) }</div>
                  </div>
                </div>
              </div>

              {/* 2. Critical Dates */ }
              <div>
                <div style={ { fontSize: '10px', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' } }>IMPORTANT DATES</div>
                <div style={ { display: 'flex', gap: '6px', flexDirection: 'column' } }>
                  <div style={ { display: 'grid', gridTemplateColumns: 'min-content 1fr', alignItems: 'center', gap: '8px', fontSize: '13px' } }>
                    <div style={ { display: 'flex', alignItems: 'center', gap: '6px', color: '#4b5563', whiteSpace: 'nowrap' } }><Calendar size={ 14 } strokeWidth={ 2 } /> Next Call</div>
                    <div style={ { fontWeight: 500, color: '#111827', textAlign: 'right' } }>
                      { (displayData.nextCallMeetingDate && !isNaN(new Date(displayData.nextCallMeetingDate))) ? new Date(displayData.nextCallMeetingDate).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' }) : <span style={ { color: '#9ca3af', fontStyle: 'italic', fontSize: '12px' } }>No input</span> }
                    </div>
                  </div>
                  <div style={ { display: 'grid', gridTemplateColumns: 'min-content 1fr', alignItems: 'center', gap: '8px', fontSize: '13px' } }>
                    <div style={ { display: 'flex', alignItems: 'center', gap: '6px', color: '#4b5563', whiteSpace: 'nowrap' } }><ActivityIcon size={ 14 } strokeWidth={ 2 } /> Last Activity</div>
                    <div style={ { fontWeight: 500, color: '#111827', textAlign: 'right' } }>
                      { (displayData.lastActivityDate && !isNaN(new Date(displayData.lastActivityDate))) ? new Date(displayData.lastActivityDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : <span style={ { color: '#9ca3af', fontStyle: 'italic', fontSize: '12px' } }>No input</span> }
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. Scores */ }
              <div>
                <div style={ { 
                  fontSize: '11px', 
                  color: '#1f2937', 
                  fontWeight: 700, 
                  textTransform: 'uppercase', 
                  letterSpacing: '0.02em', 
                  marginBottom: '8px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                } }>📊 Scores</div>
                <div style={ { display: 'flex', gap: '12px', flexDirection: 'column' } }>

                  {/* Call Score */ }
                  <div>
                    <div style={ { display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' } }>
                      <span style={ { color: '#4b5563' } }>Call Score</span>
                      <span style={ { fontWeight: 600, color: getCallScoreColor(displayData.callScore) } }>
                        { (displayData.callScore === null || displayData.callScore === undefined) ?
                          <span style={ { color: '#9ca3af', fontStyle: 'italic', fontSize: '12px', fontWeight: 400 } }>No input</span> :
                          `${displayData.callScore}/10`
                        }
                      </span>
                    </div>
                    <div style={ { width: '100%', height: '6px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' } }>
                      <div style={ { width: `${displayData.callScore ? (displayData.callScore / 10) * 100 : 0}%`, height: '100%', background: getCallScoreColor(displayData.callScore) } }></div>
                    </div>
                  </div>

                  {/* Risk Score */ }
                  <div>
                    <div style={ { display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' } }>
                      <span style={ { color: '#4b5563' } }>Risk Score</span>
                      <span style={ { fontWeight: 600, color: getRiskColor(displayData.riskScore.value) } }>
                        { (displayData.riskScore.value === null || displayData.riskScore.value === undefined) ?
                          <span style={ { color: '#9ca3af', fontStyle: 'italic', fontSize: '12px', fontWeight: 400 } }>No input</span> :
                          `${displayData.riskScore.label} (${displayData.riskScore.value})`
                        }
                      </span>
                    </div>
                    <div style={ { width: '100%', height: '6px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' } }>
                      <div style={ { width: `${displayData.riskScore.value || 0}%`, height: '100%', background: getRiskColor(displayData.riskScore.value) } }></div>
                    </div>
                  </div>

                  {/* Engagement Score */ }
                  <div>
                    <div style={ { display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' } }>
                      <span style={ { color: '#4b5563' } }>Engagement Score</span>
                      <span style={ { fontWeight: 600, color: getCallScoreColor(displayData.customerEngagementScore) } }>
                        { (displayData.customerEngagementScore === null || displayData.customerEngagementScore === undefined) ?
                          <span style={ { color: '#9ca3af', fontStyle: 'italic', fontSize: '12px', fontWeight: 400 } }>No input</span> :
                          `${displayData.customerEngagementScore}/10`
                        }
                      </span>
                    </div>
                    <div style={ { width: '100%', height: '6px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden' } }>
                      <div style={ { width: `${displayData.customerEngagementScore ? (displayData.customerEngagementScore / 10) * 100 : 0}%`, height: '100%', background: getCallScoreColor(displayData.customerEngagementScore) } }></div>
                    </div>
                  </div>

                </div>
              </div>

              {/* 4. MEDDICC Status */ }
              <div>
                <div style={ { fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' } }>
                  MEDDICC Status
                  <div style={ { position: 'relative', display: 'inline-flex' } }>
                    <div
                      onMouseEnter={ () => setShowMeddicInfo(true) }
                      onMouseLeave={ () => setShowMeddicInfo(false) }
                      style={ {
                        width: '14px', height: '14px', borderRadius: '50%',
                        background: '#396afc', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '9px', fontWeight: 700, cursor: 'pointer', flexShrink: 0
                      } }
                    >i</div>
                    { showMeddicInfo && (
                      <div style={ {
                        position: 'fixed',
                        background: '#1e293b', color: '#fff', borderRadius: '8px',
                        padding: '12px', width: '200px', zIndex: 99999,
                        fontSize: '11px', lineHeight: '1.5',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        marginTop: '18px',
                        marginLeft: '-90px',
                      } }>
                        <div style={ { fontWeight: 700, marginBottom: '6px', fontSize: '12px' } }>MEDDIC Sales Methodology</div>
                        <div style={ { color: '#94a3b8', marginBottom: '8px' } }>A framework to qualify and prioritize leads effectively.</div>
                        <div>• <strong>M</strong>etrics</div>
                        <div>• <strong>E</strong>conomic Buyer</div>
                        <div>• <strong>D</strong>ecision Criteria</div>
                        <div>• <strong>D</strong>ecision Process</div>
                        <div>• <strong>I</strong>dentify Pain</div>
                        <div>• <strong>C</strong>hampion</div>
                      </div>
                    ) }
                  </div>
                </div>
                <div style={ { display: 'flex', gap: '4px', justifyContent: 'space-between' } }>
                  { ['M', 'E', 'D', 'D', 'I', 'C'].map((char, index) => {
                    const key = meddiccKeys[index];
                    const isPresent = displayData.meddicc[key]?.present;

                    return (
                      <div key={ index } style={ {
                        width: '28px',
                        height: '28px',
                        background: isPresent ? '#22c55e' : '#e5e7eb',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: isPresent ? '#ffffff' : '#6b7280'
                      } }>
                        { char }
                      </div>
                    );
                  }) }
                </div>
              </div>

            </div>

          </div>

          {/* Center Content - Maximize Width */ }
          <div style={ { flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 } }>

            {/* Navbar Tabs */ }
            <div style={ {
              background: '#fff',
              borderRadius: '8px',
              padding: '0 16px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              gap: '24px',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              flexShrink: 0
            } }>
              { tabs.map((tab) => (
                <div
                  key={ tab }
                  onClick={ () => setActiveTab(tab) }
                  style={ {
                    color: activeTab === tab ? '#ef4444' : '#6b7280',
                    fontWeight: 600,
                    fontSize: '12px',
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: activeTab === tab ? '2px solid #ef4444' : '2px solid transparent',
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em'
                  } }
                >
                  { tab }
                </div>
              )) }

            </div>

            {/* Tab Content Display */ }
            <div style={ { display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, overflowY: 'auto', minHeight: 0 } }>
              { activeTab === "Deal overview" && <DealOverview /> }
              { activeTab === "Deal health" && <DealHealth /> }
              {/* { activeTab === "Stakeholders" && <Stakeholders /> } */ }
              { activeTab === "Activity" && <Activity /> }
              { activeTab === "Products" && <Products /> }

              { activeTab === "AI Insights" && <AIInsights /> }
            </div>

          </div>

        </div>



      </div >
    </div >
  );
}